import React, { useMemo, useState } from 'react';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { useAppSelector } from '../../hooks/redux';
import { useLiveInventoryDisplay } from '../../hooks/useLiveInventoryDisplay';
import type { AmhsLiveInventoryStocker } from '../../types/amhsStream';
import { liveStockerColumn } from '../../features/simulation/liveStockerInventory';
import CheckboxMultiSelect from '../common/CheckboxMultiSelect';
import SimulationAccordion from './SimulationAccordion';

function count(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function stockerFoupCount(stocker: AmhsLiveInventoryStocker): number {
  const occupancy = count(stocker.occupancy);
  if (occupancy > 0) return occupancy;
  return new Set([
    ...columnFoupIds(stocker, 'raw'),
    ...columnFoupIds(stocker, 'processed'),
  ]).size;
}

function stockerDropdownDetail(stocker: AmhsLiveInventoryStocker): string {
  const foupCount = stockerFoupCount(stocker);
  if (foupCount === 0) return 'Empty';
  return `${foupCount} FOUP${foupCount === 1 ? '' : 's'}`;
}
function columnFoupIds(stocker: AmhsLiveInventoryStocker, kind: 'raw' | 'processed'): string[] {
  return [
    ...new Set(
      (stocker.ports ?? [])
        .filter((port) => port.column === kind)
        .flatMap((port) => port.foupIds ?? [])
        .filter(Boolean)
    ),
  ].sort();
}

const FillMeter: React.FC<{
  label: string;
  occupancy: number;
  capacity: number;
  tone: 'raw' | 'processed';
}> = ({ label, occupancy, capacity, tone }) => {
  const fill = capacity > 0 ? Math.min(100, (occupancy / capacity) * 100) : 0;
  const color = tone === 'raw' ? 'bg-neutral-800' : 'bg-red-900';
  return (
    <div>
      <div className="mb-1 flex justify-between gap-2 text-[10px]">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-500">
          {occupancy} / {capacity}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${fill}%` }} />
      </div>
    </div>
  );
};

const FoupList: React.FC<{ label: string; ids: string[] }> = ({ label, ids }) => (
  <div className="mt-2">
    <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    {ids.length > 0 ? (
      <div className="mt-1 flex flex-wrap gap-1">
        {ids.map((id) => (
          <span
            key={id}
            className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[9px] text-slate-700"
          >
            {id}
          </span>
        ))}
      </div>
    ) : (
      <div className="mt-0.5 text-[9px] text-slate-400">Empty</div>
    )}
  </div>
);

const LiveInventoryPanel: React.FC = () => {
  const [selectedStockerIds, setSelectedStockerIds] = useState<string[]>([]);
  const inventory = useLiveInventoryDisplay();
  const kpis = useAppSelector((state) => state.simulation.live.kpis);
  useAppSelector((state) => state.simulation.simulationTime);
  const lastStockerDrops = useAppSelector((state) => state.simulation.live.lastStockerDrops);

  const stockers = useMemo(
    () => [...(inventory?.stockers ?? [])].sort((a, b) => a.stockerId.localeCompare(b.stockerId)),
    [inventory?.stockers]
  );
  const visibleStockers = useMemo(() => {
    if (selectedStockerIds.length === 0) return stockers;
    const selected = new Set(selectedStockerIds);
    return stockers.filter((stocker) => selected.has(stocker.stockerId));
  }, [selectedStockerIds, stockers]);
  const gates = useMemo(
    () =>
      [...(inventory?.gates ?? [])].sort((a, b) =>
        String(a.role ?? a.name ?? a.portId).localeCompare(String(b.role ?? b.name ?? b.portId))
      ),
    [inventory?.gates]
  );
  const cargo = Object.values(liveOhtRuntimeStore.getFrame().byOhtId)
    .filter((runtime) => runtime.carriedFoupId)
    .map((runtime) => [runtime.ohtId, runtime.carriedFoupId!] as const)
    .sort(([a], [b]) => a.localeCompare(b));
  const foupsInSystem = count(kpis.foupsInSystem ?? inventory?.foupsInSystem);

  return (
    <div className="space-y-2">
      <CheckboxMultiSelect
        label="Stockers"
        noneLabel="All"
        value={selectedStockerIds}
        options={stockers.map((stocker) => ({
          value: stocker.stockerId,
          label: stocker.stockerId,
          detail: stockerDropdownDetail(stocker),
        }))}
        onChange={setSelectedStockerIds}
        labelClassName="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-slate-400"
        buttonClassName="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-[11px] font-medium text-slate-800 outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-300"
      />

      <SimulationAccordion
      title="STOCKER INVENTORY"
      ariaLabel="Stocker inventory"
      defaultOpen
      headerExtra={
        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700">
          {foupsInSystem} ACTIVE FOUP{foupsInSystem === 1 ? '' : 'S'}
        </span>
      }
    >
      <div className="space-y-3 p-3">
        <section>
          <h4 className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            Entry / Exit
          </h4>
          {gates.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {gates.map((gate) => (
                <div key={gate.portId} className="rounded-lg border border-slate-200 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[10px] font-semibold text-slate-700">
                      {gate.name ?? gate.role ?? gate.portId}
                    </span>
                    <span className="tabular-nums text-[10px] font-semibold text-slate-900">
                      {count(gate.occupancy)} / {count(gate.capacity)}
                    </span>
                  </div>
                  <div className="mt-1 text-[9px] text-slate-400">
                    {gate.role === 'entry'
                      ? `${count(gate.released)} released`
                      : `${count(gate.delivered)} delivered`}
                    {gate.inFab != null ? ` · ${count(gate.inFab)} in fab` : ''}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-slate-400">No gate inventory received yet.</p>
          )}
        </section>

        <section>
          <h4 className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            Stockers
          </h4>
          {visibleStockers.length > 0 ? (
            <div className="space-y-2">
              {visibleStockers.map((stocker) => {
                const raw = liveStockerColumn(stocker, 'raw');
                const processed = liveStockerColumn(stocker, 'processed');
                const rawIds = columnFoupIds(stocker, 'raw');
                const processedIds = columnFoupIds(stocker, 'processed');
                const lastDrop = lastStockerDrops[stocker.stockerId];
                return (
                  <article
                    key={stocker.stockerId}
                    className="rounded-lg border border-slate-200 p-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-[11px] font-semibold text-slate-800">
                          {stocker.stockerId}
                          {stocker.processType ? ` · ${stocker.processType}` : ''}
                        </div>
                        <div className="text-[9px] text-slate-400">{stocker.status ?? '—'}</div>
                      </div>
                      <span className="shrink-0 tabular-nums text-[11px] font-semibold text-slate-700">
                        {count(stocker.occupancy)} / {count(stocker.capacity)}
                      </span>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <FillMeter
                        label="Raw"
                        occupancy={count(raw.occupancy)}
                        capacity={count(raw.capacity)}
                        tone="raw"
                      />
                      <FillMeter
                        label="Processed"
                        occupancy={count(processed.occupancy)}
                        capacity={count(processed.capacity)}
                        tone="processed"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FoupList label="Raw FOUPs" ids={rawIds} />
                      <FoupList label="Processed FOUPs" ids={processedIds} />
                    </div>

                    {lastDrop && (
                      <div className="mt-2 rounded bg-emerald-50 px-2 py-1.5 text-[9px] text-emerald-800">
                        Last drop: <span className="font-mono">{lastDrop.foupId}</span> by{' '}
                        <span className="font-mono">{lastDrop.ohtId}</span> at{' '}
                        <span className="font-mono">{lastDrop.portId}</span>
                        {lastDrop.column ? ` · ${lastDrop.column}` : ''} · t=
                        {lastDrop.simTimeS.toFixed(1)}s
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : stockers.length > 0 ? (
            <p className="text-[10px] text-slate-400">No stockers match the current filter.</p>
          ) : (
            <p className="text-[10px] text-slate-400">No stocker inventory received yet.</p>
          )}
        </section>

        <section>
          <h4 className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            OHT Cargo
          </h4>
          {cargo.length > 0 ? (
            <div className="space-y-1">
              {cargo.map(([ohtId, foupId]) => (
                <div
                  key={ohtId}
                  className="flex items-center justify-between rounded bg-slate-50 px-2 py-1.5 text-[10px]"
                >
                  <span className="font-mono font-medium text-slate-700">{ohtId}</span>
                  <span className="text-slate-400">carrying</span>
                  <span className="font-mono font-semibold text-emerald-700">{foupId}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-slate-400">No OHT is carrying a FOUP.</p>
          )}
        </section>
      </div>
    </SimulationAccordion>
    </div>
  );
};

export default LiveInventoryPanel;
